import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { formatInTimeZone } from 'date-fns-tz';
import type MetricsService from 'garaje/services/metrics';
import {
  DATE_FNS_MM_DD_YYYY,
  DATE_FNS_YYYY_MM_DD,
  DEFAULT_TIMEZONE,
  endOfDayFromQP,
  ensurePlainJsDate,
  startOfDayFromQP,
  type CalendarDate,
} from 'garaje/utils/date-fns-tz-utilities';
import { reads } from 'macro-decorators';

import type { VisitorsInvitesGroupsIndexRouteModel } from './route';

export default class VisitorsInvitesGroupsIndexController extends Controller {
  @service declare metrics: MetricsService;

  declare model: VisitorsInvitesGroupsIndexRouteModel;

  queryParams = ['startDate', 'endDate', 'page', 'query'];

  @tracked startDate: string | null = null;
  @tracked endDate: string | null = null;
  @tracked page = 1;
  @tracked query = '';

  @reads('model.currentLocation.timezone', DEFAULT_TIMEZONE) timezone!: string;

  //TODO: Make date selection boilerplate logic reusable across components.
  get todayAsString(): string {
    return formatInTimeZone(new Date(), this.timezone, DATE_FNS_YYYY_MM_DD);
  }

  get startDateWithDefault(): string {
    return this.startDate ?? this.todayAsString;
  }

  get endDateWithDefault(): string {
    return this.endDate ?? this.todayAsString;
  }

  get isToday(): boolean {
    return this.startDateWithDefault === this.todayAsString && this.endDateWithDefault === this.todayAsString;
  }

  get selectedDateRangeWithDefault(): string {
    return this.isToday ? 'Today' : 'Custom';
  }

  get start(): Date {
    return startOfDayFromQP(this.startDateWithDefault, this.timezone);
  }

  get end(): Date {
    return endOfDayFromQP(this.endDateWithDefault, this.timezone);
  }

  get startDateFormat(): string {
    return formatInTimeZone(this.start, this.timezone, DATE_FNS_MM_DD_YYYY);
  }

  get endDateFormat(): string {
    return formatInTimeZone(this.end, this.timezone, DATE_FNS_MM_DD_YYYY);
  }

  get isSingleDay(): boolean {
    return this.startDateFormat === this.endDateFormat;
  }

  @action
  selectDateRange(start: CalendarDate, end: CalendarDate): void {
    const startDate = ensurePlainJsDate(start, this.timezone);
    const endDate = ensurePlainJsDate(end, this.timezone);

    const startFormatted = formatInTimeZone(startDate, this.timezone, DATE_FNS_YYYY_MM_DD);
    const endFormatted = formatInTimeZone(endDate, this.timezone, DATE_FNS_YYYY_MM_DD);

    if (startFormatted > endFormatted) {
      return;
    }

    this.startDate = startFormatted;
    this.endDate = endFormatted;
    this.page = 1;

    this.metrics.trackEvent('Group Invites Date Range Filtered', {
      start_date: this.startDate,
      end_date: this.endDate,
      preset: this.selectedDateRangeWithDefault,
    });
  }
}
